// tslint:disable:no-duplicate-string
import {
  dynamicCollectionRendererKey,
  dynamicElementsIconKey,
  dynamicElementsSimpleTextKey,
  dynamicElementsTabs,
} from '@epicuro-next/constants/dynamic-elements';
import {
  CONFIGURATIONS_PROVIDER_KEY,
  LIST_ACCESS_PROVIDER_KEY,
  OPENED_LIST_DATA_PROVIDER_KEY,
} from '@epicuro-next/data-providers/providers-registry';
import {
  IDynamicItem,
  LogicOperator,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { PRODUCT_LIST_SUBROUTES } from '@epicuro-next/navigation/constants';

import {
  ProductListConfigurationKeys,
  PRODUCT_LIST_NAMESPACE,
} from '../../../product-list-rules';
import {
  epicShowIf,
  hasLandingPageNewsFeedId,
  hasModalNewsFeedId,
  hasSpotlightNewsFeedId,
  isEmptyAvailableProducts,
  isListAllowedForEdit,
  isListNotDeleted,
  isNotLandingPagePromoted,
  isNotModalPromoted,
  isNotPowerList,
  isNotSpotlightPromoted,
  isOwnerOfOpenedList,
  isPowerList,
} from '../if-statements/if-statements';

export const promoteSpotlightIf = [
  LogicOperator.AND,
  [
    [
      LogicOperator.OR,
      [
        [
          LogicOperator.EQ,
          [
            {
              provider: CONFIGURATIONS_PROVIDER_KEY,
              property: {
                namespace: PRODUCT_LIST_NAMESPACE,
                featureName:
                  ProductListConfigurationKeys.AllowSpotlightPromotion,
                defaultValue: false,
              },
            },
            true,
          ],
        ],
        hasSpotlightNewsFeedId,
      ],
    ],
    isListAllowedForEdit,
  ],
];

export const promoteModalIf = [
  LogicOperator.AND,
  [
    [
      LogicOperator.OR,
      [
        [
          LogicOperator.EQ,
          [
            {
              provider: CONFIGURATIONS_PROVIDER_KEY,
              property: {
                namespace: PRODUCT_LIST_NAMESPACE,
                featureName: ProductListConfigurationKeys.AllowModalPromotion,
                defaultValue: false,
              },
            },
            true,
          ],
        ],
        hasModalNewsFeedId,
      ],
    ],
    isListAllowedForEdit,
  ],
];

export const promoteLandingPageIf = [
  LogicOperator.AND,
  [
    [
      LogicOperator.OR,
      [
        [
          LogicOperator.EQ,
          [
            {
              provider: CONFIGURATIONS_PROVIDER_KEY,
              property: {
                namespace: PRODUCT_LIST_NAMESPACE,
                featureName:
                  ProductListConfigurationKeys.AllowLandingPagePromotion,
                defaultValue: false,
              },
            },
            true,
          ],
        ],
        hasLandingPageNewsFeedId,
      ],
    ],
    isPowerList,
    isListAllowedForEdit,
  ],
];

export const promoteEmailIf = [
  LogicOperator.AND,
  [
    [
      LogicOperator.EQ,
      [
        {
          provider: CONFIGURATIONS_PROVIDER_KEY,
          property: {
            namespace: PRODUCT_LIST_NAMESPACE,
            featureName: ProductListConfigurationKeys.AllowEmailPromotion,
            defaultValue: false,
          },
        },
        true,
      ],
    ],
    isOwnerOfOpenedList,
    isListNotDeleted,
  ],
];

export const isPromoteSpotlightDisabled = [
  LogicOperator.AND,
  [
    [
      LogicOperator.EQ,
      [
        {
          provider: CONFIGURATIONS_PROVIDER_KEY,
          property: {
            namespace: PRODUCT_LIST_NAMESPACE,
            featureName:
              ProductListConfigurationKeys.DisablePromotionNoProducts,
            defaultValue: false,
          },
        },
        true,
      ],
    ],
    [LogicOperator.AND, [isEmptyAvailableProducts, isNotPowerList]],
    isNotSpotlightPromoted,
  ],
];

export const isPromoteModalDisabled = [
  LogicOperator.AND,
  [
    [
      LogicOperator.EQ,
      [
        {
          provider: CONFIGURATIONS_PROVIDER_KEY,
          property: {
            namespace: PRODUCT_LIST_NAMESPACE,
            featureName:
              ProductListConfigurationKeys.DisablePromotionNoProducts,
            defaultValue: false,
          },
        },
        true,
      ],
    ],
    [LogicOperator.AND, [isEmptyAvailableProducts, isNotPowerList]],
    isNotModalPromoted,
  ],
];

export const isPromoteEmailDisabled = [
  LogicOperator.AND,
  [
    [
      LogicOperator.EQ,
      [
        {
          provider: CONFIGURATIONS_PROVIDER_KEY,
          property: {
            namespace: PRODUCT_LIST_NAMESPACE,
            featureName:
              ProductListConfigurationKeys.DisablePromotionNoProducts,
            defaultValue: false,
          },
        },
        true,
      ],
    ],
    [LogicOperator.AND, [isEmptyAvailableProducts, isNotPowerList]],
  ],
];

export const epicShowDisabled = [
  LogicOperator.OR,
  [
    isEmptyAvailableProducts,
    [
      LogicOperator.EQ,
      [
        {
          provider: OPENED_LIST_DATA_PROVIDER_KEY,
          property: 'isVideoMedia',
        },
        false,
      ],
    ],
  ],
];

const promotedViaEmail = [
  LogicOperator.EQ,
  [
    {
      provider: OPENED_LIST_DATA_PROVIDER_KEY,
      property: 'promotedViaEmail',
    },
    true,
  ],
];

const isNotPromotedViaEmail = [
  LogicOperator.EQ,
  [
    {
      provider: OPENED_LIST_DATA_PROVIDER_KEY,
      property: 'promotedViaEmail',
    },
    false,
  ],
];

export const tabsConfigDefault: IDynamicItem = {
  contentTypeId: dynamicElementsTabs,
  inputs: {
    items: [
      {
        label: {
          routerLink: ['details'],
          children: [
            {
              contentTypeId: dynamicCollectionRendererKey,
              styles: {
                classes: ['d-flex', 'align-items-center'],
              },
              inputs: {
                items: [
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'tag'],
                    },
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@listTab',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
      {
        label: {
          routerLink: ['promote-news-feed'],
          children: [
            {
              contentTypeId: 'dynamic-collections-renderer',
              styles: {
                classes: ['d-flex', 'align-items-center'],
              },
              inputs: {
                items: [
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'megaphone'],
                    },
                    if: isNotSpotlightPromoted,
                  },
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fac', 'megaphone-shout'],
                    },
                    if: hasSpotlightNewsFeedId,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotionSpotlightTab',
                    },
                    if: isNotSpotlightPromoted,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotedViaSpotlightTab',
                    },
                    if: hasSpotlightNewsFeedId,
                  },
                ],
              },
            },
          ],
        },
        disabled: isPromoteSpotlightDisabled,
        if: promoteSpotlightIf,
        tooltip: '@@disablePromotionTab',
        tooltipEnabled: isPromoteSpotlightDisabled,
      },
      {
        label: {
          routerLink: [PRODUCT_LIST_SUBROUTES.promoteViaModalFeed],
          children: [
            {
              contentTypeId: 'dynamic-collections-renderer',
              styles: {
                classes: ['d-flex', 'align-items-center'],
              },
              inputs: {
                items: [
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'message'],
                    },
                    if: isNotModalPromoted,
                  },
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'message-lines'],
                    },
                    if: hasModalNewsFeedId,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotionModalTab',
                    },
                    if: isNotModalPromoted,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotedViaModalTab',
                    },
                    if: hasModalNewsFeedId,
                  },
                ],
              },
            },
          ],
        },
        disabled: isPromoteModalDisabled,
        if: promoteModalIf,
        tooltip: '@@disablePromotionTab',
        tooltipEnabled: isPromoteModalDisabled,
      },
      {
        label: {
          routerLink: [PRODUCT_LIST_SUBROUTES.promoteViaLandingPageFeed],
          children: [
            {
              contentTypeId: 'dynamic-collections-renderer',
              styles: {
                classes: ['d-flex', 'align-items-center'],
              },
              inputs: {
                items: [
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'memo'],
                    },
                    if: isNotLandingPagePromoted,
                  },
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'memo-circle-check'],
                    },
                    if: hasLandingPageNewsFeedId,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotionLandingPageTab',
                    },
                    if: isNotLandingPagePromoted,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotedViaLandingPageTab',
                    },
                    if: hasLandingPageNewsFeedId,
                  },
                ],
              },
            },
          ],
        },
        if: promoteLandingPageIf,
      },
      {
        label: {
          routerLink: ['promote-email'],
          children: [
            {
              contentTypeId: 'dynamic-collections-renderer',
              styles: {
                classes: ['d-flex', 'align-items-center'],
              },
              inputs: {
                items: [
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'envelope'],
                    },
                    if: isNotPromotedViaEmail,
                  },
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'envelope-open-text'],
                    },
                    if: promotedViaEmail,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotionEmailTab',
                    },
                    if: isNotPromotedViaEmail,
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@promotedEmailTab',
                    },
                    if: promotedViaEmail,
                  },
                ],
              },
            },
          ],
        },
        if: promoteEmailIf,
        disabled: isPromoteEmailDisabled,
        tooltip: '@@disablePromotionTab',
        tooltipEnabled: isPromoteEmailDisabled,
      },
      {
        label: {
          routerLink: ['epic-show'],
          children: [
            {
              contentTypeId: 'dynamic-collections-renderer',
              styles: {
                classes: ['d-flex', 'align-items-center'],
              },
              inputs: {
                items: [
                  {
                    contentTypeId: dynamicElementsIconKey,
                    styles: {
                      classes: ['mr-md-3'],
                    },
                    inputs: {
                      icon: ['fas', 'video'],
                    },
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@createEpicShowTab',
                    },
                    if: [
                      LogicOperator.EQ,
                      [
                        {
                          provider: LIST_ACCESS_PROVIDER_KEY,
                          property: 'isEpicShow',
                        },
                        false,
                      ],
                    ],
                  },
                  {
                    contentTypeId: dynamicElementsSimpleTextKey,
                    styles: {
                      classes: ['d-none', 'd-md-inline'],
                    },
                    inputs: {
                      text: '@@editEpicShowTab',
                    },
                    if: [
                      LogicOperator.EQ,
                      [
                        {
                          provider: LIST_ACCESS_PROVIDER_KEY,
                          property: 'isEpicShow',
                        },
                        true,
                      ],
                    ],
                  },
                ],
              },
            },
          ],
        },
        disabled: epicShowDisabled,
        if: epicShowIf,
        tooltip: {
          provider: OPENED_LIST_DATA_PROVIDER_KEY,
          property: 'epicShowTooltip',
        },
      },
    ],
  },
};
